import { useState, useEffect } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { globalFilterSelector } from "reduxStore/globalFilter/globalFilter.selectors";

interface UseFetchOptions<TParams, TResponse> {
  serviceFn: (params: TParams & { clientId: string }) => Promise<TResponse>;
  errorMessage?: string;
  onSuccess?: (data: TResponse) => void;
  initialParams?: Omit<TParams, "clientId">;
  deps?: any[];
}

export function useFetch<TParams extends object, TResponse>({
  serviceFn,
  errorMessage = "Operation failed",
  onSuccess,
  initialParams,
  deps = [],
}: UseFetchOptions<TParams, TResponse>) {
  const { clientId } = useSelector(globalFilterSelector, shallowEqual);
  const [data, setData] = useState<TResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = async (params?: Omit<TParams, "clientId">) => {
    setIsLoading(true);
    try {
      const serviceParams = {
        ...(params || initialParams || {}),
        clientId,
      } as TParams & { clientId: string };

      const result = await serviceFn(serviceParams);

      if (result) {
        setData(result);
        onSuccess?.(result);
      }
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(initialParams);
  }, [clientId, ...deps]);

  return {
    data,
    setData,
    isLoading,
    refetch: fetchData,
  };
}
